import { z } from 'zod'
import { countEffectiveChars } from '@/lib/text/count'
import {
  getExclusiveTags,
  normalizeTags,
} from '@/features/editor/lib/editor-tags'
import type {
  EditorDraft,
  EditorKnowledge,
} from '@/features/editor/types/editor.types'

const entrySchema = (limit: number, label: string) =>
  z
    .object({
      content: z.string().optional().default(''),
    })
    .superRefine((entry, ctx) => {
      const content = entry.content ?? ''
      if (countEffectiveChars(content) > limit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label}单条不超过${limit}字（不含标点与空格）`,
        })
      }
    })

const editorSaveSchema = z
  .object({
    draft: z.object({
      title: z.string().trim().min(1, '游戏名为必填字段'),
      narrativeCore: z.string().optional().default(''),
      tags: z.array(z.string()).default([]),
    }),
    knowledge: z.object({
      worldInfo: z.array(entrySchema(1800, '世界信息')).default([]),
      characterProfiles: z.array(entrySchema(2500, '角色档案')).default([]),
    }),
  })
  .superRefine((data, ctx) => {
    if (countEffectiveChars(data.draft.narrativeCore) > 2000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '叙事核心不超过2000字（不含标点与空格）',
      })
    }

    const normalizedTags = normalizeTags(data.draft.tags)
    const identityTags = getExclusiveTags(normalizedTags, 'identity')
    if (identityTags.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '玩家身份必选且只能选择一个选项（固定主角/自定主角）',
      })
    }

    const orientationTags = getExclusiveTags(normalizedTags, 'orientation')
    if (orientationTags.length !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '类型必选且只能选择一个选项（女性向/男性向/其他性向/全性向）',
      })
    }

    if (identityTags[0] === '固定主角') {
      if (data.knowledge.characterProfiles.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '固定主角模式下角色档案中至少有一条信息',
        })
      }
    }
  })

export function validateEditorSave(
  draft: EditorDraft,
  knowledge: EditorKnowledge
): string | null {
  const result = editorSaveSchema.safeParse({ draft, knowledge })
  if (result.success) return null
  return result.error.issues[0]?.message ?? '保存校验失败'
}
